import {
    DefaultApi,
    Configuration,
    GetAllDevicesRequest,
    GetAllExitNodesRequest,
    GetAllGroupsRequest,
    GetAllPreAuthKeysRequest,
    GetAllSubnetsRequest,
    GetAllTagsRequest,
    GetAllUsersRequest,
    GetChangelogRequest,
    GetPendingExitNodesRequest,
    GetPendingRoutesRequest,
    GetSingleGroupMembersRequest,
    RequiredError,
    ResponseError,
} from "src/api/generated/frontend_customer_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";
import { UUID } from "src/api/api_common";

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiCustomer = {
    accounts: {
        getAll: () => handleError(defaultApi.getAllAccounts()),
    },
    settings: {
        getGeneral: () => handleError(defaultApi.getGeneralSettings()),
    },
    users: {
        getAll: (request: GetAllUsersRequest) => handleError(defaultApi.getAllUsers({ GetAllUsersRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleUser({ uuid: uuid })),
    },
    groups: {
        getAll: (request: GetAllGroupsRequest) =>
            handleError(defaultApi.getAllGroups({ GetAllGroupsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleGroup({ uuid })),
        getMembers: (uuid: UUID, request: GetSingleGroupMembersRequest) =>
            handleError(
                defaultApi.getSingleGroupMembers({
                    uuid,
                    GetSingleGroupMembersRequest: request,
                }),
            ),
    },
    tags: {
        getAll: (request: GetAllTagsRequest) => handleError(defaultApi.getAllTags({ GetAllTagsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getTag({ uuid })),
    },
    subnets: {
        getAll: (request: GetAllSubnetsRequest) =>
            handleError(defaultApi.getAllSubnets({ GetAllSubnetsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleSubnet({ uuid })),
        getPendingRoutes: (request: GetPendingRoutesRequest) =>
            handleError(defaultApi.getPendingSubnetRoutes({ GetPendingRoutesRequest: request })),
    },
    preAuthKeys: {
        getAll: (request: GetAllPreAuthKeysRequest) =>
            handleError(defaultApi.getAllPreAuthKeys({ GetAllPreAuthKeysRequest: request })),
    },
    devices: {
        getAll: (request: GetAllDevicesRequest) =>
            handleError(defaultApi.getAllDevices({ GetAllDevicesRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleDevice({ uuid })),
        dumpDeviceState: (uuid: UUID) => handleError(defaultApi.dumpDeviceState({ uuid })),
    },
    changelog: {
        get: (request: GetChangelogRequest) => handleError(defaultApi.getChangelog({ GetChangelogRequest: request })),
    },
    accessRules: {
        getAll: () => handleError(defaultApi.getAllAccessRules()),
        getAllForDevice: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForDevice({ uuid })),
        getAllForGroup: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForGroup({ uuid })),
        getAllForUser: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForUser({ uuid })),
        getAllForOptions: () => handleError(defaultApi.getAllAccessRuleOptions()),
    },
    dns: {
        get: () => handleError(defaultApi.getGlobalDns()),
        getAllSplitDns: () => handleError(defaultApi.getSplitDns()),
        getExtraDnsByDevice: (device_uuid: UUID) => handleError(defaultApi.getDeviceExtraDns({ uuid: device_uuid })),
    },
    exitNode: {
        getAll: (request: GetAllExitNodesRequest) =>
            handleError(defaultApi.getAllApprovedExitNodes({ GetAllExitNodesRequest: request })),
        getPending: (request: GetPendingExitNodesRequest) =>
            handleError(defaultApi.getPendingExitNodes({ GetPendingExitNodesRequest: request })),
    },
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                msg = e.response.statusText;
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    Console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
